// src/pages/admin/UserDetail.jsx
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminUsersApi } from '../../api/services'
import { Button, Card, CardHeader, CardBody, StatusBadge, Badge, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

export default function UserDetail() {
  const { id } = useParams()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['admin-user', id], queryFn: () => adminUsersApi.show(id) })

  const approve = useMutation({ mutationFn: () => adminUsersApi.approve(id), onSuccess: () => { toast.success('Approved'); qc.invalidateQueries(['admin-user', id]) } })
  const suspend = useMutation({ mutationFn: () => adminUsersApi.suspend(id), onSuccess: () => { toast.success('Suspended'); qc.invalidateQueries(['admin-user', id]) } })
  const impersonate = useMutation({ mutationFn: () => adminUsersApi.impersonate(id), onSuccess: (res) => { toast.success('Impersonating — reload page'); window.location.href = '/' } })

  if (isLoading) return <PageSpinner />
  const payload = data?.data?.data || {}
  const user = payload.user || payload

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-display font-bold">{user.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={user.role==='admin'?'purple':user.role==='supplier'?'blue':'grey'}>{user.role}</Badge>
            <StatusBadge status={user.status || (user.is_active ? 'active' : 'suspended')} />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {user.role==='supplier' && user.status==='pending' && <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate()}>Approve Supplier</Button>}
          {user.is_active && user.role!=='admin' && <Button size="sm" variant="danger" loading={suspend.isPending} onClick={() => suspend.mutate()}>Suspend</Button>}
          {user.role!=='admin' && <Button size="sm" variant="secondary" loading={impersonate.isPending} onClick={() => impersonate.mutate()}>Impersonate</Button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader><span className="font-bold text-sm">Account Info</span></CardHeader>
          <CardBody className="space-y-2 text-sm">
            {[['Email', user.email],['Phone', user.phone||user.mobile],['Business', user.business_name],['GSTIN', user.gstin],['PAN', user.pan],['Joined', new Date(user.created_at).toLocaleDateString('en-IN')]].filter(([,v])=>v).map(([k,v])=>(
              <div key={k} className="flex gap-3"><span className="text-muted w-24 flex-shrink-0">{k}</span><span className="font-medium">{v}</span></div>
            ))}
          </CardBody>
        </Card>

        {payload.stats && (
          <Card>
            <CardHeader><span className="font-bold text-sm">Activity</span></CardHeader>
            <CardBody className="space-y-2 text-sm">
              {Object.entries(payload.stats).map(([k,v])=>(
                <div key={k} className="flex gap-3"><span className="text-muted w-36 flex-shrink-0 capitalize">{k.replace(/_/g,' ')}</span><span className="font-medium">{typeof v==='number'&&v>1000?`₹${Number(v).toLocaleString('en-IN')}`:v}</span></div>
              ))}
            </CardBody>
          </Card>
        )}
      </div>

      {payload.recent_orders?.length > 0 && (
        <Card className="mt-4">
          <CardHeader><span className="font-bold text-sm">Recent Orders</span></CardHeader>
          <CardBody className="p-0">
            {payload.recent_orders.map(o => (
              <div key={o.id} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-b-0 text-sm">
                <span>{o.order_number}</span>
                <div className="flex items-center gap-3">
                  <span>₹{Number(o.total_amount).toLocaleString('en-IN')}</span>
                  <StatusBadge status={o.status} />
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </div>
  )
}
